import { type FC, memo, useEffect, useMemo, useState } from "react";
import { areArraysEqualIgnoreOrder } from "widgets/utils";

import { RegionField, type RegionFieldProps } from "@/modules/entities/components/RegionField.tsx";

interface WorkflowRegionFieldProps extends Omit<RegionFieldProps, "genomeIds"> {
  // We are all connected activities to spot a genome param
  selectedGenomesIds: number[];
  isPlaybookContext?: boolean;
}

const WorkflowRegionFieldRender: FC<WorkflowRegionFieldProps> = (props) => {
  const { selectedGenomesIds, isDisabled, errorMessage, isPlaybookContext, onInternalErrorChange } = props;
  const [selectedGenomeIds, setSelectedGenomeIds] = useState<number[]>(selectedGenomesIds);

  /** Overwrites the error message for missing organism selection when the widget is used in Playbook context **/
  const computedErrorMessage = useMemo(() => {
    if (errorMessage && !selectedGenomeIds.length) {
      return isPlaybookContext
        ? "A genome selection is required"
        : "No genomes have been set here or up in the activity tree";
    }
    return errorMessage;
  }, [errorMessage, isPlaybookContext, selectedGenomeIds.length]);

  const computedDisabled = useMemo(
    () => isDisabled ?? !selectedGenomeIds.length,
    [isDisabled, selectedGenomeIds.length],
  );

  useEffect(() => {
    const newGenomeIds = selectedGenomesIds;
    // FIXME: The WorkflowParameterField renders 16 times twice (32 times) when used in Playbooks. We need to stabilize the
    // selection here to prevent re-rendering. This widget already re-renders 4 times.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedGenomeIds((current) => (areArraysEqualIgnoreOrder(current, newGenomeIds) ? current : newGenomeIds));
  }, [selectedGenomesIds]);

  return (
    <RegionField
      {...props}
      genomeIds={selectedGenomeIds}
      isDisabled={computedDisabled}
      errorMessage={computedErrorMessage}
      onInternalErrorChange={onInternalErrorChange}
    />
  );
};

export const WorkflowRegionField = memo(WorkflowRegionFieldRender);
